import React, { FunctionComponent } from "react";
import { useAccessDetailsController } from "./controller";
import { AuthZTable } from "../../../../components/authZTable";
import { Principal } from "../../models/information-asset-accesses";
import { SystemResource } from "../../models/system-resources";
import "./index.scss";
import { Routes } from "../../../../common/Routes";
import { LinkSpan } from "../../../../components/linkSpan";
import {
  InformationAssetsAction,
  sendInformationAssetsEvent,
} from "../../../../components/matomoClient";
import { GroupAppliedScopesByUsersCount } from "../../models/current-applied-scopes";

export type AccessDetailProps = { resourceId: string };

export const AccessDetails: FunctionComponent<AccessDetailProps> = (props) => {
  const controller = useAccessDetailsController(props);

  const columns = [
    {
      title: "Group name",
      key: "name",
      dataIndex: "name",
      render: (value: string, record) => (
        <div className={"asset-name"}>
          <LinkSpan
            keyId={record.id}
            to={`${Routes.InformationAssets.path}/${props.resourceId}/${record.systemId}/user-group/${record.systemUserGroupId}`}
            text={record.name}
            className={undefined}
            target={undefined}
            active={undefined}
            matomoFn={() => {
              sendInformationAssetsEvent(InformationAssetsAction.GroupDetails(value));
            }}
          />
        </div>
      ),
      onCell: () => {
        return {
          valign: "top",
        };
      },
      width: "auto",
      searchEnabled: true,
      sortEnabled: true,
    },
    {
      title: "Managed in",
      key: "managed",
      dataIndex: "systemResources",
      onCell: () => {
        return {
          valign: "top",
        };
      },
      render: (values: { type: string; systemResources: SystemResource[] }[]) => (
        <div>{values[0]?.systemResources[0]?.system.name} group</div>
      ),
      sorter: (recordA, recordB) => {
        const recordAType = recordA.systemResources[0]?.systemResources[0]?.system?.name;
        const recordBType = recordB.systemResources[0]?.systemResources[0]?.system?.name;
        return recordAType.localeCompare(recordBType);
      },
      width: "auto",
      searchEnabled: false,
      sortEnabled: true,
    },
    {
      title: "No of users",
      key: "members",
      dataIndex: "members",
      onCell: () => {
        return {
          valign: "top",
        };
      },
      render: (members: Principal[]) => (
        <>
          <div>{members.length}</div>
        </>
      ),
      width: "auto",
      searchEnabled: false,
      sortEnabled: true,
    },
    {
      title: "System Resources",
      key: "systemResources",
      dataIndex: "systemResources",
      onCell: () => {
        return {
          valign: "top",
        };
      },
      render: (values: { type: string; systemResources: SystemResource[] }[], record) => (
        <div className={"system-resources"}>
          {values.map(({ type, systemResources }) => (
            <div key={type}>
              <LinkSpan
                keyId={record.id}
                to={`${Routes.InformationAssets.path}/${props.resourceId}/systemResourceType?selectedSystem=${systemResources[0].system.name}&selectedResourceType=${type}&group=${encodeURIComponent(record.name)}`}
                text={`${type} (${systemResources.length})`}
                className={undefined}
                target={undefined}
                active={undefined}
                matomoFn={() => {
                  sendInformationAssetsEvent(
                    InformationAssetsAction.LinksClickedInAccessDetailsTabs(type, record.name)
                  );
                }}
              />
            </div>
          ))}
        </div>
      ),
      sorter: (recordA, recordB) => {
        const recordAType = recordA.systemResources[0].type;
        const recordBType = recordB.systemResources[0].type;
        return recordAType.localeCompare(recordBType);
      },
      width: "auto",
      searchEnabled: false,
      sortEnabled: true,
    },
    {
      title: "Current applied scope(s) for group members",
      key: "currentAppliedScopesWithCount",
      dataIndex: "currentAppliedScopesWithCount",
      onCell: () => {
        return {
          valign: "top",
        };
      },
      render: (values: GroupAppliedScopesByUsersCount, record) => (
        <div className={"current-applied-scopes"}>
          {values.map((currentAppliedScopeWithCount) => (
            <div key={currentAppliedScopeWithCount.displayFormat()}>
              <LinkSpan
                keyId={record.id}
                to={`${Routes.InformationAssets.path}/${props.resourceId}/${
                  record.systemId
                }/user-group/${
                  record.systemUserGroupId
                }?scope=${currentAppliedScopeWithCount.appliedScope.displayFormat()}`}
                text={currentAppliedScopeWithCount.displayFormat()}
                className={undefined}
                target={undefined}
                active={undefined}
                matomoFn={() => {
                  sendInformationAssetsEvent(
                    InformationAssetsAction.LinksClickedInAccessDetailsTabs(
                      currentAppliedScopeWithCount.appliedScope.displayFormat(),
                      record.name
                    )
                  );
                }}
              />
            </div>
          ))}
        </div>
      ),
      width: "auto",
      searchEnabled: true,
      sortEnabled: false,
      onFilter: (value: string, record) => {
        const appliedScope = record.currentAppliedScopesWithCount
          .map((currentAppliedScopeWithCount) => {
            return currentAppliedScopeWithCount.displayFormat();
          })
          .toString();
        return appliedScope.toLowerCase().includes(value.toLowerCase());
      },
    },
  ];

  return (
    <div className={"AccessDetails"}>
      <AuthZTable
        className={"access-details"}
        name={"access-details"}
        id={"access-details"}
        columns={columns}
        dataSource={controller.getTableData()}
      />
    </div>
  );
};
